import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Loader2, Mail, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Logo } from '../components/Logo';
import { formatApiError } from '../lib/apiError';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resending, setResending] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setNeedsVerification(false);

    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.fullName}!`);
      navigate(user.role === 'admin' ? '/admin' : user.role === 'manager' ? '/manager' : '/worker');
    } catch (error) {
      const detail = error.response?.data?.detail;
      // Structured detail = email-not-verified gate
      if (detail && typeof detail === 'object' && detail.code === 'EMAIL_NOT_VERIFIED') {
        setNeedsVerification(true);
        toast.error(detail.message || 'Please verify your email to continue.');
      } else {
        toast.error(formatApiError(error, 'Login failed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      toast.error('Enter your email first.');
      return;
    }
    setResending(true);
    try {
      await axios.post(`${API_URL}/api/auth/resend-verification`, { email });
      toast.success("If an account needs verification, we've sent a fresh email.");
    } catch (err) {
      toast.success("If an account needs verification, we've sent a fresh email.");
    } finally {
      setResending(false);
    }
  };

  const quickLogin = async (role) => {
    const credentials = role === 'manager'
      ? { email: 'manager1@postoffice.com', password: 'password123' }
      : { email: 'worker1@email.com', password: 'password123' };

    setEmail(credentials.email);
    setPassword(credentials.password);
    setLoading(true);

    try {
      const user = await login(credentials.email, credentials.password);
      toast.success(`Welcome back, ${user.fullName}!`);
      navigate(user.role === 'admin' ? '/admin' : user.role === 'manager' ? '/manager' : '/worker');
    } catch (error) {
      toast.error(formatApiError(error, 'Login failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F1F5F9] flex flex-col">
      {/* Header */}
      <header className="bg-[#0f1b3d] text-white px-6 py-5">
        <Logo size="sm" variant="light" linkTo="/" />
      </header>

      {/* Content */}
      <main className="flex-1 flex items-center justify-center p-6">
        <Card className="w-full max-w-md shadow-lg border-0">
          <CardHeader className="space-y-1 pb-6">
            <CardTitle className="text-2xl font-bold text-slate-900 font-['Public_Sans']">
              Sign In
            </CardTitle>
            <CardDescription className="text-slate-500">
              Enter your credentials to access your account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700 font-medium">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-14 rounded-xl bg-slate-50 border-slate-200 focus:ring-[#0f1b3d] focus:border-transparent"
                  data-testid="login-email-input"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-slate-700 font-medium">Password</Label>
                  <Link
                    to="/forgot-password"
                    className="text-sm text-[#DA291C] font-semibold hover:underline"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </Link>
                </div>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-14 rounded-xl bg-slate-50 border-slate-200 focus:ring-[#0f1b3d] focus:border-transparent"
                  data-testid="login-password-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full h-14 rounded-full bg-[#DA291C] hover:bg-[#b91f13] text-white font-semibold text-lg"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>

            {needsVerification && (
              <div className="mt-5 rounded-xl bg-amber-50 border border-amber-200 p-4" data-testid="email-not-verified-banner">
                <div className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-amber-700 mt-0.5 shrink-0" />
                  <div className="flex-1 text-sm text-amber-900">
                    <p className="font-semibold">Email not verified</p>
                    <p className="mt-1">Check your inbox for the verification link, or request a new one below.</p>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleResendVerification}
                      disabled={resending}
                      className="mt-3 h-10 rounded-full border-amber-700 text-amber-900 hover:bg-amber-100"
                      data-testid="resend-verification-btn"
                    >
                      {resending ? (
                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending...</>
                      ) : (
                        <><RefreshCw className="mr-2 h-4 w-4" />Resend verification email</>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-6 pt-6 border-t border-slate-200">
              <p className="text-sm text-slate-500 text-center mb-4">Quick demo login:</p>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 h-12 rounded-full border-2 border-[#0f1b3d] text-[#0f1b3d] font-medium hover:bg-slate-50"
                  onClick={() => quickLogin('manager')}
                  disabled={loading}
                  data-testid="quick-login-manager-btn"
                >
                  Manager
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 h-12 rounded-full border-2 border-[#0f1b3d] text-[#0f1b3d] font-medium hover:bg-slate-50"
                  onClick={() => quickLogin('worker')}
                  disabled={loading}
                  data-testid="quick-login-worker-btn"
                >
                  Worker
                </Button>
              </div>
            </div>

            <p className="mt-6 text-center text-slate-600">
              Don't have an account?{' '}
              <Link
                to="/register"
                className="text-[#DA291C] font-semibold hover:underline"
                data-testid="register-link"
              >
                Register
              </Link>
            </p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
